<script lang="ts">
  import { Contabilidad, type CuentaContable, type AsientoContable } from '../lib/api';

  type Seccion = 'resumen' | 'asientos' | 'catalogo';
  type Balance = { activo?: number; pasivo?: number; patrimonio?: number; pasivoMasPatrimonio?: number };
  type ER = { ingresos?: number; costos?: number; gastos?: number; utilidad?: number };

  let seccion = $state<Seccion>('resumen');
  let cuentas = $state<CuentaContable[]>([]);
  let asientos = $state<AsientoContable[]>([]);
  let balance = $state<Balance | null>(null);
  let er = $state<ER | null>(null);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let loading = $state(false);
  let mostrandoNuevo = $state(false);

  let nuevaCta = $state<CuentaContable>({ codigo: '', nombre: '', tipo: 'ACTIVO', naturaleza: 'DEUDORA', nivel: 1, permiteMov: true });
  let editandoCtaId = $state<string | null>(null);
  let nuevoAs = $state<AsientoContable>({
    fecha: new Date().toISOString().slice(0, 10),
    concepto: '',
    partidas: [
      { cuentaId: '', debe: 0, haber: 0, descripcion: '' },
      { cuentaId: '', debe: 0, haber: 0, descripcion: '' },
    ],
  });

  function fmtQ(n: number | null | undefined): string {
    return `Q ${Number(n ?? 0).toFixed(2)}`;
  }

  function nombreCuenta(id: string): string {
    const c = cuentas.find(x => x.id === id);
    return c ? `${c.codigo} ${c.nombre}` : 'Cuenta';
  }

  async function cargar() {
    loading = true;
    error = null;
    try {
      const [cts, as, bal, res] = await Promise.all([
        Contabilidad.cuentas(),
        Contabilidad.asientos(),
        Contabilidad.balance().catch(() => null),
        Contabilidad.estadoResultados().catch(() => null),
      ]);
      cuentas = (cts ?? []).filter(c => c.activa !== false);
      asientos = as ?? [];
      balance = bal;
      er = res;
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      loading = false;
    }
  }

  function editarCta(c: CuentaContable) {
    editandoCtaId = c.id ?? null;
    nuevaCta = {
      codigo: c.codigo, nombre: c.nombre, tipo: c.tipo, naturaleza: c.naturaleza,
      nivel: c.nivel ?? 1, permiteMov: c.permiteMov ?? true, activa: c.activa ?? true,
    };
  }
  function cancelarCta() {
    editandoCtaId = null;
    nuevaCta = { codigo: '', nombre: '', tipo: 'ACTIVO', naturaleza: 'DEUDORA', nivel: 1, permiteMov: true };
  }

  async function guardarCta(e: SubmitEvent) {
    e.preventDefault();
    try {
      if (editandoCtaId) await Contabilidad.actualizarCta(editandoCtaId, { ...nuevaCta });
      else await Contabilidad.crearCta({ ...nuevaCta });
      cancelarCta();
      await cargar();
    } catch (e: any) { error = e?.message; }
  }

  function agregarPartida() {
    nuevoAs.partidas = [...nuevoAs.partidas, { cuentaId: '', debe: 0, haber: 0, descripcion: '' }];
  }

  async function crearAs(e: SubmitEvent) {
    e.preventDefault();
    try {
      await Contabilidad.crearAs({ ...nuevoAs });
      nuevoAs = {
        fecha: new Date().toISOString().slice(0, 10),
        concepto: '',
        partidas: [
          { cuentaId: '', debe: 0, haber: 0, descripcion: '' },
          { cuentaId: '', debe: 0, haber: 0, descripcion: '' },
        ],
      };
      mostrandoNuevo = false;
      info = 'Asiento guardado en borrador. Pulse Registrar para que mueva saldos.';
      await cargar();
    } catch (e: any) { error = e?.message; }
  }

  async function registrar(id: string) {
    try {
      await Contabilidad.registrar(id);
      info = 'Asiento registrado. Ya mueve el resumen.';
      await cargar();
    } catch (e: any) { error = e?.message; }
  }

  async function eliminarCta(c: CuentaContable) {
    if (!confirm(`¿Quitar del catálogo ${c.codigo} ${c.nombre}?`)) return;
    try { await Contabilidad.eliminarCta(c.id!); await cargar(); }
    catch (e: any) { error = e?.message; }
  }

  $effect(() => { void cargar(); });

  const sumDebe = $derived(nuevoAs.partidas.reduce((s, p) => s + Number(p.debe || 0), 0));
  const sumHaber = $derived(nuevoAs.partidas.reduce((s, p) => s + Number(p.haber || 0), 0));
  const cuadrado = $derived(Math.abs(sumDebe - sumHaber) < 0.009 && sumDebe > 0);
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Contabilidad</h2>
      <p class="muted">Libros del hospital (ingresos, gastos, resultado). No cobra al paciente: eso está en <strong>Cobrar</strong> y <strong>Farmacia POS</strong>.</p>
    </div>
    <button type="button" class="ghost" onclick={() => cargar()} disabled={loading}>{loading ? '…' : 'Actualizar'}</button>
  </header>

  {#if error}<p class="err" role="alert">⚠ {error}</p>{/if}
  {#if info}<p class="ok" role="status">{info}</p>{/if}

  <nav class="tabs">
    <button type="button" class:on={seccion === 'resumen'} onclick={() => seccion = 'resumen'}>Resumen</button>
    <button type="button" class:on={seccion === 'asientos'} onclick={() => seccion = 'asientos'}>Asientos</button>
    <button type="button" class:on={seccion === 'catalogo'} onclick={() => seccion = 'catalogo'}>Catálogo</button>
  </nav>

  {#if seccion === 'resumen'}
    <p class="guia">Estos números salen de asientos <em>registrados</em> en libros. Un cobro en caja no aparece aquí hasta que el contador lo asiente.</p>
    <div class="kpis">
      <div class="kpi">
        <span>Ingresos</span>
        <strong>{fmtQ(er?.ingresos)}</strong>
      </div>
      <div class="kpi">
        <span>Gastos + costos</span>
        <strong>{fmtQ(Number(er?.gastos ?? 0) + Number(er?.costos ?? 0))}</strong>
      </div>
      <div class="kpi">
        <span>Utilidad</span>
        <strong class:neg={Number(er?.utilidad ?? 0) < 0}>{fmtQ(er?.utilidad)}</strong>
      </div>
      <div class="kpi">
        <span>Activo</span>
        <strong>{fmtQ(balance?.activo)}</strong>
      </div>
      <div class="kpi">
        <span>Pasivo</span>
        <strong>{fmtQ(balance?.pasivo)}</strong>
      </div>
      <div class="kpi">
        <span>Patrimonio</span>
        <strong>{fmtQ(balance?.patrimonio)}</strong>
      </div>
    </div>
    {#if asientos.length > 0}
      <h3>Últimos asientos</h3>
      <table>
        <thead><tr><th>Fecha</th><th>Concepto</th><th>Estado</th><th>Monto</th></tr></thead>
        <tbody>
          {#each asientos.slice(0, 8) as a (a.id)}
            <tr>
              <td>{a.fecha}</td>
              <td>{a.concepto}</td>
              <td>{a.estado === 'REGISTRADO' ? 'Registrado' : 'Borrador'}</td>
              <td class="num">{fmtQ(a.totalDebe)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {:else}
      <p class="empty">Aún no hay asientos. El contador los carga en la pestaña Asientos.</p>
    {/if}
  {/if}

  {#if seccion === 'asientos'}
    <div class="row-actions">
      <button type="button" class="primary" onclick={() => mostrandoNuevo = !mostrandoNuevo}>
        {mostrandoNuevo ? 'Cancelar' : 'Nuevo asiento'}
      </button>
    </div>
    {#if mostrandoNuevo}
      <form onsubmit={crearAs} class="panel">
        <p class="hint-linea">Debe = Haber (mismo monto a izquierda y derecha). Ejemplo: cobros del día → Debe Bancos, Haber Ingresos.</p>
        <div class="row">
          <label class="field"><span>Fecha</span><input type="date" bind:value={nuevoAs.fecha} required /></label>
          <label class="field grow"><span>Concepto</span><input placeholder="Ej. Cobros de caja del día" bind:value={nuevoAs.concepto} required /></label>
          <button type="button" class="ghost" onclick={agregarPartida}>+ Línea</button>
          <button type="submit" class="primary" disabled={!cuadrado}>
            {cuadrado ? `Guardar ${fmtQ(sumDebe)}` : `Debe ${fmtQ(sumDebe)} ≠ Haber ${fmtQ(sumHaber)}`}
          </button>
        </div>
        <table>
          <thead><tr><th>Cuenta</th><th>Debe</th><th>Haber</th><th>Nota</th></tr></thead>
          <tbody>
            {#each nuevoAs.partidas as p, idx (idx)}
              <tr>
                <td>
                  <select bind:value={p.cuentaId} required>
                    <option value="">— cuenta —</option>
                    {#each cuentas as c (c.id)}
                      <option value={c.id}>{c.codigo} {c.nombre}</option>
                    {/each}
                  </select>
                </td>
                <td><input type="number" min="0" step="0.01" bind:value={p.debe} /></td>
                <td><input type="number" min="0" step="0.01" bind:value={p.haber} /></td>
                <td><input bind:value={p.descripcion} /></td>
              </tr>
            {/each}
          </tbody>
        </table>
      </form>
    {/if}

    <table>
      <thead><tr><th>Fecha</th><th>Concepto</th><th>Estado</th><th>Debe</th><th>Haber</th><th></th></tr></thead>
      <tbody>
        {#each asientos as a (a.id)}
          <tr>
            <td>{a.fecha}</td>
            <td>
              {a.concepto}
              {#if a.partidas?.length}
                <div class="sub">
                  {#each a.partidas as p, i (i)}
                    {nombreCuenta(p.cuentaId)}
                    {#if Number(p.debe) > 0} Debe {fmtQ(p.debe)}{/if}
                    {#if Number(p.haber) > 0} Haber {fmtQ(p.haber)}{/if}{#if i < a.partidas.length - 1}; {/if}
                  {/each}
                </div>
              {/if}
            </td>
            <td>{a.estado === 'REGISTRADO' ? 'Registrado' : 'Borrador'}</td>
            <td class="num">{fmtQ(a.totalDebe)}</td>
            <td class="num">{fmtQ(a.totalHaber)}</td>
            <td>
              {#if a.estado === 'BORRADOR'}
                <button type="button" class="primary sm" onclick={() => registrar(a.id!)}>Registrar</button>
              {/if}
            </td>
          </tr>
        {:else}
          <tr><td colspan="6" class="empty">Sin asientos.</td></tr>
        {/each}
      </tbody>
    </table>
  {/if}

  {#if seccion === 'catalogo'}
    <form onsubmit={guardarCta} class="row">
      <label class="field"><span>Código</span><input placeholder="1102" bind:value={nuevaCta.codigo} required /></label>
      <label class="field grow"><span>Nombre</span><input placeholder="Bancos" bind:value={nuevaCta.nombre} required /></label>
      <label class="field"><span>Tipo</span>
        <select bind:value={nuevaCta.tipo}>
          <option>ACTIVO</option><option>PASIVO</option><option>PATRIMONIO</option>
          <option>INGRESO</option><option>GASTO</option><option>COSTO</option>
        </select>
      </label>
      <label class="field"><span>Naturaleza</span>
        <select bind:value={nuevaCta.naturaleza}><option>DEUDORA</option><option>ACREEDORA</option></select>
      </label>
      <button type="submit" class="primary">{editandoCtaId ? 'Guardar' : 'Agregar'}</button>
      {#if editandoCtaId}<button type="button" class="ghost" onclick={cancelarCta}>Cancelar</button>{/if}
    </form>
    <table>
      <thead><tr><th>Código</th><th>Nombre</th><th>Tipo</th><th>Saldo</th><th></th></tr></thead>
      <tbody>
        {#each cuentas as c (c.id)}
          <tr>
            <td>{c.codigo}</td>
            <td>{c.nombre}</td>
            <td>{c.tipo}</td>
            <td class="num">{fmtQ(c.saldoActual)}</td>
            <td>
              <button type="button" class="ghost sm" onclick={() => editarCta(c)}>Editar</button>
              <button type="button" class="ghost sm" onclick={() => eliminarCta(c)}>Quitar</button>
            </td>
          </tr>
        {:else}
          <tr><td colspan="5" class="empty">Sin cuentas. Agregue el plan arriba (Bancos, Ingresos, Gastos…).</td></tr>
        {/each}
      </tbody>
    </table>
  {/if}
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; }
  .muted { margin: 0.25rem 0 0; color: var(--text-secondary, #6b7280); font-size: 0.9rem; }
  .tabs { display: flex; flex-wrap: wrap; gap: .4rem; margin-bottom: 1rem; }
  .tabs button {
    padding: .4rem .8rem; border-radius: 8px; border: 1px solid var(--border, #e5e7eb);
    background: var(--bg-elevated, #fff); color: inherit; font: inherit; font-weight: 600; cursor: pointer;
  }
  .tabs button.on { border-color: #16a34a; color: #166534; background: color-mix(in srgb, #16a34a 14%, transparent); }
  .guia { color: var(--text-secondary, #6b7280); font-size: .92rem; margin: 0 0 1rem; max-width: 44rem; }
  .kpis { display: grid; grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr)); gap: .75rem; margin-bottom: 1.25rem; }
  .kpi { border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: .75rem 1rem; background: var(--bg-elevated, #fff); }
  .kpi span { display: block; font-size: .7rem; text-transform: uppercase; letter-spacing: .04em; color: var(--text-secondary, #6b7280); }
  .kpi strong { font-size: 1.2rem; color: #15803d; }
  .kpi strong.neg { color: #dc2626; }
  .row { display: flex; gap: .75rem; flex-wrap: wrap; margin: .6rem 0; align-items: flex-end; }
  .row-actions { margin-bottom: .75rem; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field.grow { flex: 1; min-width: 12rem; }
  .field span { font-size: .72rem; font-weight: 600; color: var(--text-secondary, #6b7280); text-transform: uppercase; }
  .panel { border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: .85rem 1rem; margin-bottom: 1rem; background: var(--bg-elevated, #fff); }
  .hint-linea { margin: 0 0 .6rem; font-size: .85rem; color: var(--text-secondary, #6b7280); }
  input, select, button {
    padding: .5rem .65rem; border-radius: 8px; border: 1px solid var(--border, #e5e7eb);
    font: inherit; background: var(--bg-elevated, #fff); color: inherit;
  }
  button { cursor: pointer; font-weight: 600; }
  button:disabled { opacity: .5; cursor: not-allowed; }
  button.primary { background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border: 0; }
  button.ghost { background: var(--bg-elevated, #fff); }
  button.sm { font-size: .8rem; padding: .3rem .55rem; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: var(--text-secondary, #6b7280); padding: .45rem .55rem; background: color-mix(in srgb, #16a34a 16%, transparent); }
  td { padding: .45rem .55rem; border-top: 1px solid var(--border, #f3f4f6); font-size: .9rem; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .sub { font-size: .78rem; color: var(--text-secondary, #6b7280); margin-top: .2rem; font-weight: 500; }
  .err { color: #dc2626; font-weight: 600; }
  .ok { color: #166534; font-weight: 600; }
  .empty { color: var(--text-secondary, #9ca3af); font-style: italic; text-align: center; }
  h3 { margin: 1rem 0 .4rem; font-size: .95rem; color: #166534; }
  td input, td select { width: 100%; }
</style>
